import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useCreerEmploye, useModifierEmploye, useEmploye } from '../hooks/useEmployes';
import { administrationApi } from '../../administration/api/administration.api';

const schema = z.object({
  nom: z.string().min(2, 'Nom requis'),
  prenoms: z.string().min(2, 'Prénom(s) requis'),
  fonction: z.string().min(2, 'Fonction requise'),
  salaireBase: z.number().positive('Salaire base doit être positif'),
  numeroCnss: z.string().optional(),
  ifu: z.string().optional(),
  npi: z.string().optional(),
  lieuTravail: z.string().min(1, 'Lieu de travail requis'),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  telephone: z.string().optional(),
  numeroMatricule: z.string().optional(),
  dateEmbauche: z.string().min(1, 'Date d\'embauche requise'),
  dateDebauche: z.string().optional(),
  idProjet: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export function EmployeFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdition = !!id;

  const { data: employe } = useEmploye(id);
  const creer = useCreerEmploye();
  const modifier = useModifierEmploye(id ?? '');

  const { data: projets = [] } = useQuery({
    queryKey: ['projets', 'actifs'],
    queryFn: () => administrationApi.listerProjets({ actif: true }),
  });

  const { data: organisation } = useQuery({
    queryKey: ['organisation-moi'],
    queryFn: () => import('axios').then(({ default: axios }) =>
      axios.get('/api/v1/paie/administration/organisation/moi').then(r => r.data?.data ?? null).catch(() => null)
    ),
  });

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: employe ? {
      nom: employe.nom,
      prenoms: employe.prenoms,
      fonction: employe.fonction,
      salaireBase: employe.salaireBase,
      numeroCnss: employe.numeroCnss,
      ifu: employe.ifu,
      npi: employe.npi,
      lieuTravail: employe.lieuTravail,
      email: employe.email ?? '',
      telephone: employe.telephone,
      numeroMatricule: employe.numeroMatricule,
      dateEmbauche: employe.dateEmbauche.substring(0, 10),
      dateDebauche: employe.dateDebauche ? employe.dateDebauche.substring(0, 10) : '',
      idProjet: employe.idProjet ?? '',
    } : undefined,
  });

  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
      email: data.email || undefined,
      idProjet: data.idProjet || undefined,
    };
    if (isEdition) {
      await modifier.mutateAsync(payload);
      navigate(`/paie/employes/${id}`);
    } else {
      const e = await creer.mutateAsync(payload);
      navigate(`/paie/employes/${e.idEmploye}`);
    }
  };

  const champs: Array<[keyof FormData, string, string]> = [
    ['nom', 'Nom', 'text'],
    ['prenoms', 'Prénom(s)', 'text'],
    ['fonction', 'Fonction / Poste', 'text'],
    ['salaireBase', 'Salaire de base (FCFA)', 'number'],
    ['numeroCnss', 'Numéro CNSS', 'text'],
    ['ifu', 'IFU', 'text'],
    ['npi', 'NPI', 'text'],
    ['lieuTravail', 'Lieu de travail', 'text'],
    ['email', 'Email (pour création compte)', 'email'],
    ['telephone', 'Téléphone', 'tel'],
    ['numeroMatricule', 'Numéro matricule', 'text'],
    ['dateEmbauche', 'Date d\'embauche', 'date'],
    ['dateDebauche', 'Date de débauchage (si applicable)', 'date'],
  ];

  const mutErr = (isEdition ? modifier.error : creer.error) as any;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-content mb-6">
        {isEdition ? 'Modifier l\'employé' : 'Nouvel employé'}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-surface rounded-lg border border-border p-6 space-y-4">
        {organisation && (
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Organisation</label>
            <div className="w-full border border-border rounded px-3 py-2 text-sm bg-surface-raised text-content-subtle">
              {organisation.nom}
            </div>
          </div>
        )}

        {champs.map(([field, label, type]) => (
          <div key={field}>
            <label className="block text-sm font-medium text-content-muted mb-1">{label}</label>
            <input
              type={type}
              {...register(field, type === 'number' ? { valueAsNumber: true } : undefined)}
              className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors[field] && <p className="text-danger text-xs mt-1">{(errors[field] as any).message}</p>}
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Affectation projet</label>
          <select
            {...register('idProjet')}
            className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Fonctionnement (sans projet)</option>
            {projets.map((p: any) => (
              <option key={p.idProjet} value={p.idProjet}>
                {p.code} — {p.nom}
              </option>
            ))}
          </select>
        </div>

        {mutErr && <p className="text-danger text-sm bg-danger-light p-3 rounded">{mutErr.message ?? 'Erreur'}</p>}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Enregistrement...' : (isEdition ? 'Enregistrer' : 'Créer l\'employé')}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-border rounded text-sm hover:bg-surface-raised">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}
